/**
 * "Mente du outlook.com?" under the e-mail field (2026-09-29). A hotel typed 16 of 24 staff in at
 * "outloock.com", a domain with no mail server, so none of them could log in, reset a password or
 * get a notice. The warning never blocks: the address may be right, and the button fixes it in
 * one click.
 *
 * A domain one or two edits (Damerau: a swap counts as one) from a common mail domain is taken for
 * a typo of it. Two edits only for the longer names, and the real neighbours are listed, so
 * live.se or ymail.com is never "corrected".
 */
import { T } from '@/i18n';

const COMMON = ['gmail.com', 'outlook.com', 'hotmail.com', 'live.no', 'online.no', 'yahoo.com', 'icloud.com'];
// real domains that sit within reach of one above
const REAL = new Set([
  'mail.com',
  'email.com',
  'ymail.com',
  'gmx.com',
  'hotmail.no',
  'hotmail.se',
  'hotmail.dk',
  'outlook.no',
  'outlook.se',
  'outlook.dk',
  'yahoo.no',
  'yahoo.se',
  'yahoo.dk',
  'live.se',
  'live.dk',
  'live.nl',
  'live.com',
  'live.fi',
  'online.de',
  'online.fr',
  'online.nl',
]);

/** Optimal string alignment distance: insertions, deletions, substitutions and adjacent swaps. */
function distance(a: string, b: string): number {
  const d = Array.from({ length: a.length + 1 }, (_, i) =>
    Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0)),
  );
  const at = (i: number, j: number) => d[i]?.[j] ?? 0;
  for (let i = 1; i <= a.length; i++)
    for (let j = 1; j <= b.length; j++) {
      let v = Math.min(
        at(i - 1, j) + 1,
        at(i, j - 1) + 1,
        at(i - 1, j - 1) + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1])
        v = Math.min(v, at(i - 2, j - 2) + 1);
      (d[i] as number[])[j] = v;
    }
  return at(a.length, b.length);
}

/** The common domain the address was probably meant for, or null. */
export function likelyDomain(email: string): string | null {
  const at = email.trim().lastIndexOf('@');
  if (at < 1) return null;
  const domain = email
    .trim()
    .slice(at + 1)
    .toLowerCase();
  if (!domain || COMMON.includes(domain) || REAL.has(domain)) return null;
  let best: string | null = null;
  let bestD = 3;
  for (const c of COMMON) {
    const k = distance(domain, c);
    if (k <= (c.length >= 9 ? 2 : 1) && k < bestD) {
      best = c;
      bestD = k;
    }
  }
  return best;
}

/** The address with its domain replaced. */
export const withDomain = (email: string, domain: string) =>
  `${email.trim().slice(0, email.trim().lastIndexOf('@'))}@${domain}`;

/** The warning and its one-click fix, or nothing. */
export function EmailTypo({ value, onFix }: { value: string; onFix: (fixed: string) => void }) {
  const d = likelyDomain(value ?? '');
  if (!d) return null;
  return (
    <output className="fwarn" data-testid="email-typo">
      {T('Mente du {d}?', { d })}{' '}
      <button type="button" className="lnk" onClick={() => onFix(withDomain(value, d))}>
        {T('Bruk {d}', { d })}
      </button>
    </output>
  );
}
